import { useEffect, useState } from 'react';

export interface Route {
  /** e.g. "/toplama/0192…" */
  readonly path: string;
  readonly parts: readonly string[];
  readonly query: URLSearchParams;
}

function current(): Route {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const [path = '/', search = ''] = raw.split('?');
  return { path, parts: path.split('/').filter(Boolean), query: new URLSearchParams(search) };
}

// a screen with work in hand is asked before the route moves on (a menu link, the phone's back gesture)
let leaveGuard: ((to: string) => boolean) | null = null;
let shown = typeof window === 'undefined' ? '' : window.location.hash;

/** Hash routing: works from any folder and needs no server rules. */
export function useRoute(): Route {
  const [route, setRoute] = useState(current);
  useEffect(() => {
    const update = () => {
      const hash = window.location.hash;
      if (hash !== shown && leaveGuard?.(hash.replace(/^#/, '') || '/')) {
        // the back gesture: the screen stays and asks; the address goes back to it (the next listener sees no change)
        history.replaceState(history.state, '', shown || `${window.location.pathname}${window.location.search}`);
        return;
      }
      shown = hash;
      setRoute(current());
    };
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  return route;
}

export function navigate(to: string): void {
  window.location.hash = to;
}

/**
 * While a screen has work in hand: `ask` gets the route the person is leaving for and returns true
 * to stay (the screen asks, then calls leave()). Returns the release.
 */
export function guardLeave(ask: (to: string) => boolean): () => void {
  // a link (the menu, the bell) is asked before the address changes: the history gets no extra entry
  const onClick = (event: MouseEvent) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hash = link.getAttribute('href') ?? '#';
    if (hash === window.location.hash || leaveGuard !== ask) return;
    if (ask(hash.replace(/^#/, '') || '/')) event.preventDefault();
  };
  leaveGuard = ask;
  document.addEventListener('click', onClick, true);
  return () => {
    document.removeEventListener('click', onClick, true);
    if (leaveGuard === ask) leaveGuard = null;
  };
}

/** Goes on to `to` past the screen's guard (it has asked). */
export function leave(to: string): void {
  leaveGuard = null;
  navigate(to);
}
